import Vue from 'vue';
import CONFIG from 'Config';
import { getCookie } from 'Utils/session';
import { isNil } from 'Utils/common';
import { httpClientCMS } from 'Api/microservice-http-clients';
import BaseFromModel from './BaseFormModel';

const fieldMap = {
  first_name: 'firstname',
  last_name: 'lastname',
};

const EMAIL_ERROR_TEXT = 'Your email is considered as invalid. Please re-check your email. If it is correct, <a href="/company/contact-us">contact us</a> please.';

export default class BaseHSFormModel extends BaseFromModel {
  constructor() {
    super();

    this.$storeSubmittedStatus = false;
    Vue.set(this, '$submitted', false);
    this._setIsFormSubmitted();
    this.$formName = '';
    this.$hsFormId = null;
  }

  get $url() {
    return `${CONFIG.urls.hubspotForm}/${CONFIG.hubspotPortalId}/`;
  }

  get $path() {
    return `${this.$hsFormId}`;
  }

  _addRulesByServerResponse(errors) {
    this._setGeneralValidationError(errors);
  }

  _getHSContext() {
    return {
      hutk: getCookie('hubspotutk'),
      pageUri: window.location.href,
      pageName: document.title,
    };
  }

  _getHSFieldsData() {
    const data = this._mapFields(this.$changedData);
    const result = [];
    // Clear for null or undefined fields
    Object.entries(data).forEach(([name, value]) => {
      if (!isNil(value)) result.push({ name, value });
    });
    return result;
  }

  _mapFields(data) {
    const newData = { ...data };
    Object.entries(fieldMap).forEach(([from, to]) => {
      if (newData[from]) {
        newData[to] = newData[from];
        delete newData[from];
      }
    });
    return newData;
  }

  _getRequestData() {
    return {
      context: this._getHSContext(),
      fields: this._getHSFieldsData(),
    };
  }

  _getGeneralValidationError(errors) {
    let generalError = '';
    // Handle error messages from hubspot
    if (errors?.errors && Array.isArray(errors.errors)) {
      const hsErrors = [];
      // each message
      errors?.errors.forEach((fieldError) => {
        // If it email error, change to const text
        if (fieldError?.errorType === 'INVALID_EMAIL') hsErrors.push(EMAIL_ERROR_TEXT);
        // otherwise, push message to array
        else if (fieldError?.message) hsErrors.push(fieldError.message);
      });
      // join messages.
      // Message from super call is skipped
      generalError = hsErrors.join('. ');
    } else {
      // If no message, try to get to call super method
      generalError = super._getGeneralValidationError(errors);
    }
    return generalError;
  }

  $set(name, value, model) {
    // If setting prop is email and no email yet
    // then check from storage is form was submitted
    // This is for case when email copy from profile and it happen after model created
    if (name === 'email' && !this.email && value) {
      setTimeout(this._setIsFormSubmitted.bind(this));
    }
    return super.$set(name, value, model);
  }

  _onSendSuccess(response) {
    this._setIsFormSubmitted(true);
    return super._onSendSuccess(response);
  }

  _getIsFormSubmitted() {
    return localStorage.getItem(`hs-form-submitted-${this.$hsFormId}`) === this.email;
  }

  _setIsFormSubmitted(isFormSubmitted) {
    if (isFormSubmitted === undefined && this.$storeSubmittedStatus) {
      this.$submitted = this._getIsFormSubmitted();
    } else {
      if (this.$storeSubmittedStatus) {
        localStorage.setItem(`hs-form-submitted-${this.$hsFormId}`, this.email);
      }
      this.$submitted = Boolean(isFormSubmitted);
    }
  }

  /**
   * Send form data to CMS
   * Using during sending data to HubSpot
   * so to do not loss data when hubspot do not accept client email
   * @returns {Q.Promise<any> | Function | any | Promise<any> | undefined|Promise<void>}
   * @private
   */
  _sendFormToCMS() {
    // Do not send form data if not form name
    if (!this.$formName) return Promise.resolve();
    // eslint-disable-next-line camelcase
    const { email, first_name, last_name, ...data } = this.$changedData;
    const sendData = {
      form_name: this.$formName,
      email,
      first_name,
      last_name,
      data,
    };
    return httpClientCMS.post('/form', sendData).catch(error => error);
  }

  _beforeSend(...args) {
    const res = super._beforeSend(...args);
    this._sendFormToCMS();
    return res;
  }
}
